import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import { ChevronLeft, Plus, X, Save, Copy, Download, Trash2, Loader2, Tag, Edit2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CustomerSelect from "@/components/CustomerSelect";

const MT_LABEL = { exact: "Tam", phrase: "Sıralı", broad: "Geniş" };
const MT_COLOR = { exact: "#16A34A", phrase: "#4F46E5", broad: "#F59E0B" };

export default function KeywordListDetailPage() {
  const { listId } = useParams();
  const nav = useNavigate();
  const [list, setList] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [tab, setTab] = useState("positives");
  const [newKw, setNewKw] = useState("");
  const [newMt, setNewMt] = useState("phrase");
  const [editing, setEditing] = useState({ name: false, customer: false });
  const [draftName, setDraftName] = useState("");
  const [draftCustomer, setDraftCustomer] = useState("");

  const load = async () => {
    try {
      const [l, c] = await Promise.all([apiClient.get(`/keyword-lists/${listId}`), apiClient.get("/customers")]);
      setList(l.data); setCustomers(c.data || []);
      setDraftName(l.data.name); setDraftCustomer(l.data.customer_id || "");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [listId]); // eslint-disable-line

  if (!list) return <div className="p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={16} className="animate-spin inline mr-2"/>Yükleniyor…</div>;

  const items = list[tab] || [];
  const customerName = customers.find(c=>c.id===list.customer_id)?.name || "—";

  const addManual = async () => {
    const txt = newKw.trim();
    if (!txt) return;
    try {
      await apiClient.post(`/keyword-lists/${listId}/items`, {
        target: tab,
        items: txt.split("\n").map(t=>t.trim()).filter(Boolean).map(k => ({ keyword: k, match_type: newMt })),
      });
      setNewKw(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeItem = async (kw) => {
    try {
      await apiClient.delete(`/keyword-lists/${listId}/items`, { params: { target: tab, keyword: kw } });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateMatchType = async (kw, mt) => {
    try {
      await apiClient.put(`/keyword-lists/${listId}/items`, { keyword: kw, match_type: mt }, { params: { target: tab, keyword: kw } });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveMeta = async () => {
    try {
      await apiClient.put(`/keyword-lists/${listId}`, { name: draftName, customer_id: draftCustomer || null });
      setEditing({ name: false, customer: false }); load(); toast.success("Güncellendi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtKw = (kw, mt) => {
    const t = (mt || "broad").toLowerCase();
    if (t === "exact") return `[${kw}]`;
    if (t === "phrase") return `"${kw}"`;
    return kw;
  };
  const copyAll = () => {
    const text = items.map(k => fmtKw(k.keyword, k.match_type)).join("\n");
    navigator.clipboard.writeText(text); toast.success(`${items.length} kelime kopyalandı`);
  };
  const dlCsv = () => {
    const csv = "Keyword,Match Type,Group\n" + items.map(k => `"${(k.keyword||"").replace(/"/g,'""')}","${k.match_type||""}","${(k.group||"").replace(/"/g,'""')}"`).join("\n");
    const blob = new Blob([csv], {type:"text/csv;charset=utf-8"});
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${list.name}-${tab}.csv`; a.click();
  };

  return (
    <div className="space-y-5" data-testid="kw-list-detail">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/ai-araclar/anahtar-kelime/listeler" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>Kayıtlı Listeler</Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A] truncate max-w-[300px]">{list.name}</span>
      </div>

      <div className="ky-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            {editing.name ? (
              <div className="flex gap-2 items-center mb-2">
                <input value={draftName} onChange={e=>setDraftName(e.target.value)} className="ky-input ky-input-sm flex-1 max-w-md text-lg font-bold" autoFocus/>
                <button onClick={saveMeta} className="ky-btn ky-btn-accent ky-btn-sm"><Save size={12}/></button>
                <button onClick={()=>setEditing({...editing, name:false})} className="ky-btn ky-btn-ghost ky-btn-sm"><X size={12}/></button>
              </div>
            ) : (
              <h1 className="text-xl font-bold mb-1 flex items-center gap-2"><Tag size={18} className="text-[#4F46E5]"/>{list.name}
                <button onClick={()=>setEditing({...editing, name:true})} className="ky-btn ky-btn-ghost ky-btn-icon"><Edit2 size={12}/></button>
              </h1>
            )}
            {editing.customer ? (
              <div className="flex gap-2 items-center">
                <div className="w-72"><CustomerSelect customers={customers} value={draftCustomer || null} onChange={(v) => setDraftCustomer(v || "")} placeholder="— müşteri yok —" size="sm" testid="kw-list-customer"/></div>
                <button onClick={saveMeta} className="ky-btn ky-btn-accent ky-btn-sm"><Save size={12}/></button>
                <button onClick={()=>setEditing({...editing, customer:false})} className="ky-btn ky-btn-ghost ky-btn-sm"><X size={12}/></button>
              </div>
            ) : (
              <div className="text-xs text-[#64748B] space-x-2">
                <span>Müşteri: <strong className="text-[#0F172A]">{customerName}</strong></span>
                <button onClick={()=>setEditing({...editing, customer:true})} className="text-[#4F46E5] underline">değiştir</button>
                <span>· {(list.positives||[]).length} pozitif · {(list.negatives||[]).length} negatif</span>
              </div>
            )}
          </div>
          <button onClick={()=>nav(`/ai-araclar/reklam-metni/yeni?list_id=${list.id}`)} className="ky-btn ky-btn-accent" data-testid="use-in-ad-copy">
            <Sparkles size={13}/>Reklam Metni Üret
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#ECECEF]">
        <button onClick={()=>setTab("positives")} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab==="positives"?"border-[#16A34A] text-[#16A34A]":"border-transparent text-[#64748B]"}`} data-testid="tab-positives">Pozitif ({(list.positives||[]).length})</button>
        <button onClick={()=>setTab("negatives")} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab==="negatives"?"border-[#EF4444] text-[#EF4444]":"border-transparent text-[#64748B]"}`} data-testid="tab-negatives">Negatif ({(list.negatives||[]).length})</button>
      </div>

      <div className="ky-card p-4 space-y-3">
        <h3 className="font-semibold text-sm">Kelime Ekle (manuel)</h3>
        <p className="text-[11px] text-[#64748B]">Birden fazla kelimeyi her satıra bir tane yazarak toplu ekleyebilirsin.</p>
        <textarea value={newKw} onChange={e=>setNewKw(e.target.value)} rows={3} placeholder={`örn:\nimplant fiyatları\nortodonti tedavisi\n...`} className="ky-textarea text-sm" data-testid="manual-kw-input"/>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#64748B]">Eşleşme:</span>
          {["exact","phrase","broad"].map(t => (
            <button key={t} onClick={()=>setNewMt(t)} className={`px-2.5 py-1 rounded-md text-xs font-medium border-2 ${newMt===t?"border-[#4F46E5] bg-[#EEF2FF] text-[#4F46E5]":"border-[#ECECEF] text-[#64748B]"}`}>{MT_LABEL[t]}</button>
          ))}
          <div className="flex-1"/>
          <button onClick={addManual} className="ky-btn ky-btn-accent ky-btn-sm" data-testid="add-manual-kw"><Plus size={12}/>Ekle</button>
        </div>
      </div>

      <div className="ky-card p-0 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-[#ECECEF] flex items-center justify-between">
          <h3 className="font-semibold text-sm">{tab === "positives" ? "Pozitif Kelimeler" : "Negatif Kelimeler"} ({items.length})</h3>
          <div className="flex gap-1.5">
            <button onClick={copyAll} disabled={!items.length} className="ky-btn ky-btn-outline ky-btn-sm"><Copy size={11}/>Kopyala</button>
            <button onClick={dlCsv} disabled={!items.length} className="ky-btn ky-btn-outline ky-btn-sm"><Download size={11}/>CSV</button>
          </div>
        </div>
        {items.length === 0 && <div className="p-10 text-center text-xs text-[#94A3B8]">Henüz kelime yok</div>}
        {items.map((it, i) => (
          <div key={i} className="px-4 py-2.5 border-b border-[#ECECEF] last:border-b-0 flex items-center gap-3 hover:bg-[#FAFAFB]" data-testid={`item-${i}`}>
            <span className="font-mono text-sm font-medium flex-1 truncate">{fmtKw(it.keyword, it.match_type)}</span>
            {it.group && <span className="text-[10px] text-[#64748B] bg-[#F4F4F5] px-1.5 py-0.5 rounded">{it.group}</span>}
            <select value={it.match_type || "broad"} onChange={e=>updateMatchType(it.keyword, e.target.value)} className="text-xs px-2 py-1 rounded border border-[#ECECEF] bg-white font-medium" style={{color:MT_COLOR[(it.match_type||"broad").toLowerCase()]}}>
              <option value="exact">Tam</option>
              <option value="phrase">Sıralı</option>
              <option value="broad">Geniş</option>
            </select>
            <button onClick={()=>removeItem(it.keyword)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={12}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}
